import React, { useCallback, useEffect, useMemo, useState } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import clsx from "clsx";

const RELEASE_AT = new Date(
  process.env.REACT_APP_RELEASE_AT || "2026-09-19T22:00:00-05:00"
).getTime();
const RELEASE_LABEL = "September 19, 2026 at 10:00 PM Central Time";
const EVENT_TITLE =
  "Seventeenth Annual Sand Castle Competition Snapshots Arrive on the Homepage!";
const CONTEST_ASSET_BASE =
  process.env.REACT_APP_CONTEST_ASSET_BASE ||
  "/contests/sandcastle2026_contest";

type Placement = {
  title: string;
  winners: string[];
};

type ReleaseData = {
  event: string;
  placements: Placement[];
};

type CountdownValue = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

const getCountdown = (now: number): CountdownValue => {
  let remaining = Math.max(0, RELEASE_AT - now);
  const days = Math.floor(remaining / 86_400_000);
  remaining %= 86_400_000;
  const hours = Math.floor(remaining / 3_600_000);
  remaining %= 3_600_000;
  const minutes = Math.floor(remaining / 60_000);
  const seconds = Math.floor((remaining % 60_000) / 1_000);

  return { days, hours, minutes, seconds };
};

const isReleaseData = (value: unknown): value is ReleaseData => {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as ReleaseData;
  return (
    typeof candidate.event === "string" &&
    Array.isArray(candidate.placements) &&
    candidate.placements.every(
      (placement) =>
        typeof placement.title === "string" &&
        Array.isArray(placement.winners) &&
        placement.winners.every((winner) => typeof winner === "string")
    )
  );
};

const Countdown = ({ now }: { now: number }) => {
  const countdown = useMemo(() => getCountdown(now), [now]);
  const units = [
    ["Days", countdown.days],
    ["Hours", countdown.hours],
    ["Minutes", countdown.minutes],
    ["Seconds", countdown.seconds],
  ];

  return (
    <main className="release-gate">
      <div className="release-card">
        <h1>{EVENT_TITLE}</h1>
        <p className="release-card__date">{RELEASE_LABEL}</p>
        <div
          className="countdown"
          role="timer"
          aria-label={`${countdown.days} days, ${countdown.hours} hours, ${countdown.minutes} minutes, and ${countdown.seconds} seconds until release`}
        >
          {units.map(([label, value]) => (
            <div className="countdown__unit" key={label}>
              <span className="countdown__value">
                {String(value).padStart(2, "0")}
              </span>
              <span className="countdown__label">{label}</span>
            </div>
          ))}
        </div>
        <p className="release-card__note">
          The winner list and competition snapshots will appear when the
          countdown reaches zero.
        </p>
      </div>
    </main>
  );
};

export const ImageDisplayPanel = () => {
  const [now, setNow] = useState(() => Date.now());
  const [releaseData, setReleaseData] = useState<ReleaseData | null>(null);
  const [releaseError, setReleaseError] = useState(false);
  const [selectedTitle, setSelectedTitle] = useState<string | null>(null);
  const released = now >= RELEASE_AT;

  useEffect(() => {
    if (released) {
      return;
    }

    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [released]);

  useEffect(() => {
    if (!released || releaseData) {
      return;
    }

    let cancelled = false;
    let retryTimer: number | undefined;

    const loadReleaseData = async () => {
      try {
        const response = await fetch(
          `${CONTEST_ASSET_BASE}/winners.json?release=2026`,
          { cache: "no-store" }
        );

        if (!response.ok) {
          throw new Error(`Release data returned ${response.status}`);
        }

        const data: unknown = await response.json();
        if (!isReleaseData(data)) {
          throw new Error("Release data has an unexpected format");
        }

        if (!cancelled) {
          setReleaseData(data);
          setReleaseError(false);
        }
      } catch (error) {
        if (!cancelled) {
          setReleaseError(true);
          retryTimer = window.setTimeout(loadReleaseData, 15_000);
        }
      }
    };

    loadReleaseData();
    return () => {
      cancelled = true;
      if (retryTimer !== undefined) {
        window.clearTimeout(retryTimer);
      }
    };
  }, [released, releaseData]);

  const handleItemClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setSelectedTitle(event.currentTarget.dataset.title || null);
    },
    []
  );

  if (!released) {
    return <Countdown now={now} />;
  }

  if (!releaseData) {
    return (
      <main className="release-gate">
        <div className="release-card" aria-live="polite">
          <p className="release-card__eyebrow">The countdown is complete</p>
          <h1>The Sand Castle snapshots are arriving!</h1>
          <p className="release-card__note">
            {releaseError
              ? "The gallery is not available just yet. We’ll check again automatically."
              : "Loading the winner list and snapshots…"}
          </p>
        </div>
      </main>
    );
  }

  const imageSrc = selectedTitle
    ? `${CONTEST_ASSET_BASE}/${encodeURIComponent(selectedTitle)}.jpg`
    : null;

  return (
    <main className="display-base">
      <h1 className="contest-title">{releaseData.event}</h1>
      <div className="image-container">
        {imageSrc && selectedTitle ? (
          <LazyLoadImage alt={selectedTitle} src={imageSrc} height={400} />
        ) : (
          <div className="image-placeholder">
            Select a winner below to view their room snapshot.
          </div>
        )}
      </div>
      <div className="list-panel-container">
        {releaseData.placements.map(({ title, winners }) => (
          <section className="list-panel" key={title}>
            <h2 className="list-panel__header">{title}</h2>
            <div className="list-panel-content">
              {winners.map((winner) => (
                <button
                  aria-pressed={winner === selectedTitle}
                  className={clsx(
                    "list-panel__item",
                    winner === selectedTitle && "list-panel__item--selected"
                  )}
                  data-title={winner}
                  key={winner}
                  onClick={handleItemClick}
                  type="button"
                >
                  {winner}
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>
    </main>
  );
};
